/** Job detail page — status tracking, inline results, download, delete. */

import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Paper, Grid2 as Grid, Divider, CircularProgress, Alert,
  Dialog, DialogTitle, DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { useJob, useJobResult, useDownloadUrl, useDeleteJob } from "@/hooks/useJobs";
import StatusChip from "@/components/StatusChip";
import ProgressIndicator from "@/components/ProgressIndicator";
import ResultViewer from "@/components/ResultViewer";

export default function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const { data: job, isLoading } = useJob(jobId ?? "");
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteJob = useDeleteJob();

  const isTerminal = job?.status === "complete" || job?.status === "failed" || job?.status === "completed_with_errors";

  const { data: resultData } = useJobResult(jobId ?? "", isTerminal && !!job?.resultPath);
  const downloadUrl = useDownloadUrl(jobId ?? "");

  const handleDelete = () => {
    deleteJob.mutate(jobId ?? "", { onSuccess: () => navigate("/jobs") });
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (!job) return <Alert severity="error">Job not found</Alert>;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">{job.name || "Job Detail"}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {job.resultPath && (
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadUrl.refetch().then((r) => { if (r.data?.url) window.open(r.data.url, "_blank"); })}>
              Download
            </Button>
          )}
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Status</Typography><Box><StatusChip status={job.status} /></Box></Grid>
          <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Plugin</Typography><Typography>{job.pluginName ?? job.pluginId}</Typography></Grid>
          <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Type</Typography><Typography>{job.jobType}</Typography></Grid>
          <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Created</Typography><Typography>{new Date(job.createdAt).toLocaleString()}</Typography></Grid>
        </Grid>
      </Paper>

      {job.progress && (
        <Box sx={{ mb: 3 }}>
          {/* Batch jobs use completed/total; flow jobs use currentStep/totalSteps/percentComplete */}
          {"totalSteps" in job.progress ? (
            <ProgressIndicator
              completed={job.progress.currentStep ?? 0}
              total={job.progress.totalSteps}
              label="steps"
            />
          ) : (
            <ProgressIndicator
              completed={job.progress.completed ?? 0}
              total={job.progress.total ?? 0}
              failed={job.progress.failed}
            />
          )}
        </Box>
      )}

      {job.errorMessage && <Alert severity="error" sx={{ mb: 3 }}>{job.errorMessage}</Alert>}

      {resultData && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>Result</Typography>
          <ResultViewer
            content={resultData.content}
            format={resultData.format}
            downloadOnly={resultData.downloadOnly}
            onDownload={() => downloadUrl.refetch().then((r) => { if (r.data?.url) window.open(r.data.url, "_blank"); })}
          />
        </>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete this job and its results?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
